import { ArrowRight, CheckCheck, Undo2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { APPLY_KINDS, PLATFORM_LABEL, TONE, isLocked, toggleIn } from "./meta";

const Sub = ({ id }) => (id ? <span className="font-mono text-[10px] text-slate-400">sub #{id}</span> : null);

function Product({ name, id, sub }) {
  return (
    <span className="inline-flex flex-col">
      <span className="text-slate-800">{name || <span className="italic text-slate-400">unnamed product</span>}{id ? <span className="ml-1 font-mono text-[10px] text-slate-400">{id}</span> : null}</span>
      <Sub id={sub} />
    </span>
  );
}

function Change({ r }) {
  if (r.kind === "add") return <Product name={r.product_name} id={r.product_id} sub={r.sub_id} />;
  if (r.kind === "move") return (
    <span className="flex items-center gap-2">
      <Product name={r.from_product_name} sub={r.from_sub_id} />
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-sky-500" />
      <Product name={r.product_name} id={r.product_id} sub={r.sub_id} />
    </span>);
  if (r.kind === "remove") return <Product name={r.from_product_name} id={r.product_id} sub={r.from_sub_id} />;
  if (r.kind === "create_subscription") return (
    <span className="text-slate-800">{r.product_name || "—"} plan{r.customer ? <span className="block text-[11px] text-slate-500">for {r.customer}</span> : null}
      {r.planned?.length ? <span className="block text-[11px] text-violet-700">then adds {r.planned.length} item{r.planned.length === 1 ? "" : "s"}</span> : null}
      {r.blocked && <span className="block text-[11px] text-amber-700">Apply won't create it (special billing)</span>}</span>);
  return <span className="text-slate-800">{r.message}</span>;
}

const BADGE = {
  applied: ["Applied", "border-emerald-200 bg-emerald-50 text-emerald-700"],
  skipped: ["Skipped", "border-slate-200 bg-slate-50 text-slate-600"],
  failed: ["Failed", "border-red-200 bg-red-50 text-red-700"],
  queued: ["Queued", "border-sky-200 bg-sky-50 text-sky-700"],
  undone: ["Undone", "border-violet-200 bg-violet-50 text-violet-700"],
};

function ApplyBadge({ r, onUndo }) {
  const a = r.apply;
  if (!a) return null;
  const [label, cls] = BADGE[a.status] || [a.status, BADGE.skipped[1]];
  return (
    <div className="mt-1" data-testid={`bsp-row-apply-${r.seq}`}>
      <span className={`rounded border px-1.5 py-px text-[10px] font-semibold ${cls}`}>{label}</span>
      {a.message && <span className="ml-1.5 text-[11px] text-slate-600">{a.message}</span>}
      {onUndo && a.status === "applied" && (
        <button type="button" onClick={() => onUndo([{ apply_id: a.apply_id, seq: r.seq }])} data-testid={`bsp-undo-${r.seq}`}
                className="ml-2 inline-flex items-center gap-1 rounded border border-slate-200 px-1.5 py-px text-[10px] font-semibold text-slate-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-700">
          <Undo2 className="h-3 w-3" /> Undo</button>)}
    </div>
  );
}

const CHANGE_HEAD = { add: "Billed as", move: "From → to", remove: "Billed now as", create_subscription: "Subscription", issue: "Problem" };

export function RowsTable({ kind, rows, sel, onSel, onUndo }) {
  const t = TONE[kind];
  const selectable = !!onSel && APPLY_KINDS.includes(kind);
  const free = rows.filter((r) => !isLocked(r) && !sel.domains.has(r.domain));
  const allOn = free.length > 0 && free.every((r) => sel.seqs.has(r.seq));
  const togglePage = () => {
    const n = new Set(sel.seqs);
    free.forEach((r) => (allOn ? n.delete(r.seq) : n.add(r.seq)));
    onSel({ ...sel, seqs: n });
  };
  return (
    <table className="w-full text-xs" data-testid="bsp-rows-table">
      <thead className="sticky top-0 z-10 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
        <tr>
          {selectable && <th className="w-9 py-2.5 pl-4"><Checkbox checked={allOn} disabled={!free.length} onCheckedChange={togglePage} aria-label="Tick this page" data-testid="bsp-select-page" /></th>}
          <th className={`w-[26%] py-2.5 text-left ${selectable ? "px-2" : "px-4"}`}>Domain</th><th className="w-[22%] px-3 py-2.5 text-left">Item</th>
          <th className="px-3 py-2.5 text-left">{CHANGE_HEAD[kind]}</th><th className="w-[24%] px-4 py-2.5 text-left">Notes</th></tr>
      </thead>
      <tbody>{rows.map((r) => {
        const domOn = selectable && sel.domains.has(r.domain);
        const on = domOn || (selectable && sel.seqs.has(r.seq));
        return (
          <tr key={r.seq} className={`border-t border-slate-100 align-top transition-colors ${on ? "bg-slate-50" : "hover:bg-slate-50/70"}`} data-testid={`bsp-row-${r.seq}`}>
            {selectable && <td className="py-2.5 pl-4"><Checkbox checked={on && !isLocked(r)} disabled={isLocked(r) || domOn}
              onCheckedChange={() => onSel({ ...sel, seqs: toggleIn(sel.seqs, r.seq) })} aria-label={`Tick ${r.reference}`} data-testid={`bsp-select-row-${r.seq}`} /></td>}
            <td className={`py-2.5 ${selectable ? "px-2" : "px-4"}`}>
              <div className="flex items-center gap-1.5">
                <span className={`rounded border px-1.5 py-px text-[10px] font-semibold ${t.soft}`}>{PLATFORM_LABEL[r.platform] || r.platform}</span>
                <span className="truncate font-medium text-slate-800" title={r.domain}>{r.domain || "—"}</span>
                {selectable && r.domain && (
                  <button type="button" onClick={() => onSel({ ...sel, domains: toggleIn(sel.domains, r.domain) })} data-testid={`bsp-select-domain-${r.seq}`}
                          title={domOn ? "Untick this domain" : "Tick every add, move and removal for this domain"}
                          className={`ml-auto shrink-0 rounded p-1 transition-colors ${domOn ? "bg-slate-900 text-white" : "text-slate-300 hover:bg-slate-100 hover:text-slate-600"}`}>
                    <CheckCheck className="h-3.5 w-3.5" /></button>)}
              </div>
              {r.territory && <div className="mt-0.5 truncate font-mono text-[10px] text-slate-400" title={r.territory}>{r.territory}</div>}
            </td>
            <td className="px-3 py-2.5 font-mono text-[11px] text-slate-700"><span className="break-all">{r.reference || (r.customer && r.kind === "issue" ? r.customer : "—")}</span>
              {r.description && <div className="font-sans text-[10px] text-slate-500">{r.description}</div>}
              {r.item_id && <div className="text-[10px] text-slate-400">item #{r.item_id}</div>}</td>
            <td className="px-3 py-2.5"><Change r={r} /></td>
            <td className="px-4 py-2.5 text-[11px] text-slate-500">{r.kind !== "issue" && r.kind !== "add" && r.message ? <div>{r.message}</div> : null}{r.detail}
              <ApplyBadge r={r} onUndo={onUndo} /></td>
          </tr>);
      })}
      </tbody>
    </table>
  );
}
